import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Bell,
  LayoutDashboard,
  Map as MapIcon,
  Save,
  Settings,
  Shield,
  Video
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firstAllowedPath } from '../utils/rbacDefaultRoute';
import { canAccessSecurityAdmin } from '../utils/rbacSecurityAccess';
import {
  rbacApi,
  RbacModuleAction,
  RbacModulePermission,
  RbacRoleDetail,
  RbacRoleSummary
} from '../services/rbacApi';
import { showError, showSuccess } from '../utils/notifications';
import './RolesPage.css';

/** Submódulos por module_key (alineados con users_sub_modules / users_module_actions). */
const MODULE_DEFAULT_ACTIONS: Record<string, Array<{ actionKey: string; label: string }>> = {
  xtam_vms: [
    { actionKey: 'streaming', label: 'Ver stream' },
    { actionKey: 'recordings_preview', label: 'Previsualización de grabaciones' },
    { actionKey: 'recordings_download', label: 'Descarga de grabaciones' },
    { actionKey: 'analytics', label: 'Analíticas' }
  ],
  alarmas: [{ actionKey: 'alerts_buttons_eu', label: 'Alertas botón EU' }],
  administracion: [
    { actionKey: 'sites', label: 'Sitios' },
    { actionKey: 'history', label: 'Históricos' },
    { actionKey: 'miniiot', label: 'MiniIOT' },
    { actionKey: 'cameras', label: 'Cámaras' },
    { actionKey: 'inventory', label: 'Inventario' }
  ],
  seguridad: [
    { actionKey: 'roles', label: 'Roles' },
    { actionKey: 'users', label: 'Usuarios' }
  ]
};

function normalizeModules(modules: RbacModulePermission[]): RbacModulePermission[] {
  return modules.map((m: RbacModulePermission) => {
    const defs = MODULE_DEFAULT_ACTIONS[m.moduleKey];
    if (!defs) return m;
    const existing = new Map(m.actions.map((a: RbacModuleAction) => [a.actionKey, a]));
    const merged = defs.map((def) => {
      const hit = existing.get(def.actionKey);
      return hit ?? { actionKey: def.actionKey, label: def.label, enabled: false };
    });
    return { ...m, actions: merged, recordingMaxHoursBack: m.recordingMaxHoursBack ?? null };
  });
}

function moduleIcon(key: string) {
  const size = 20;
  switch (key) {
    case 'dashboards':
      return <BarChart3 size={size} />;
    case 'xtam_vms':
      return <Video size={size} />;
    case 'mapa':
      return <MapIcon size={size} />;
    case 'alarmas':
      return <Bell size={size} />;
    case 'administracion':
      return <Settings size={size} />;
    case 'seguridad':
      return <Shield size={size} />;
    default:
      return <LayoutDashboard size={size} />;
  }
}

const MODULE_KEYS_WITH_SUBACTIONS = new Set(Object.keys(MODULE_DEFAULT_ACTIONS));

function buildSummary(modules: RbacModulePermission[]): string {
  const names = modules
    .filter((m) => {
      if (!m.canAccess) return false;
      if (MODULE_KEYS_WITH_SUBACTIONS.has(m.moduleKey)) {
        return m.actions.some((a) => a.enabled);
      }
      return true;
    })
    .map((m) => m.label);
  if (names.length === 0) return 'Sin acceso a módulos configurado.';
  return `Puede acceder a: ${names.join(', ')}.`;
}

function RolesSwitch({
  checked,
  onChange,
  ariaLabel
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      className={`roles-switch-btn ${checked ? 'roles-switch-btn--on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="roles-switch-track" aria-hidden>
        <span className="roles-switch-thumb" />
      </span>
    </button>
  );
}

const RolesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [roles, setRoles] = useState<RbacRoleSummary[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<RbacRoleDetail | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['xtam_vms']));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && !canAccessSecurityAdmin(user)) {
      showError('Solo administradores pueden gestionar roles');
      navigate(firstAllowedPath(user.allowedModules, false, user.allowedActions), {
        replace: true
      });
    }
  }, [user, navigate]);

  const loadRoles = useCallback(async () => {
    try {
      const list = await rbacApi.getRoles();
      const valid = list.filter((r: RbacRoleSummary) => Number.isFinite(r.id) && r.id > 0);
      setRoles(valid);
      setSelectedId((prev) => {
        if (valid.length === 0) return null;
        const prevNum = prev == null ? null : Number(prev);
        if (
          prevNum != null &&
          Number.isFinite(prevNum) &&
          valid.some((r: RbacRoleSummary) => r.id === prevNum)
        ) {
          return prevNum;
        }
        return valid[0].id;
      });
    } catch (e: any) {
      showError(e.response?.data?.error?.message || e.message || 'Error al cargar roles');
      setRoles([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    if (selectedId === null || !Number.isFinite(selectedId) || selectedId < 1) {
      return;
    }
    const roleId = selectedId;
    let cancelled = false;
    (async () => {
      try {
        const detail = await rbacApi.getRoleDetail(roleId);
        if (!cancelled) {
          setDraft({
            ...detail,
            modules: normalizeModules(detail.modules)
          });
        }
      } catch (e: any) {
        if (!cancelled) {
          setDraft(null);
          showError(e.response?.data?.error?.message || e.message || 'Error al cargar el rol');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const summary = useMemo(() => (draft ? buildSummary(draft.modules) : ''), [draft]);

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setModuleAccess = (moduleKey: string, canAccess: boolean) => {
    if (!draft) return;
    setDraft({
      ...draft,
      modules: draft.modules.map((m) => {
        if (m.moduleKey !== moduleKey) return m;
        if (!canAccess) {
          return {
            ...m,
            canAccess: false,
            actions: m.actions.map((a) => ({ ...a, enabled: false })),
            recordingMaxHoursBack: null
          };
        }
        return { ...m, canAccess: true };
      })
    });
  };

  const setActionEnabled = (moduleKey: string, actionKey: string, enabled: boolean) => {
    if (!draft) return;
    setDraft({
      ...draft,
      modules: draft.modules.map((m: RbacModulePermission) => {
        if (m.moduleKey !== moduleKey) return m;
        return {
          ...m,
          actions: m.actions.map((a: RbacModuleAction) =>
            a.actionKey === actionKey ? { ...a, enabled } : a
          )
        };
      })
    });
  };

  const setRecordingMaxHoursBack = (moduleKey: string, value: number | null) => {
    if (!draft) return;
    setDraft({
      ...draft,
      modules: draft.modules.map((m: RbacModulePermission) => {
        if (m.moduleKey !== moduleKey) return m;
        return { ...m, recordingMaxHoursBack: value };
      })
    });
  };

  const handleSave = async () => {
    if (!draft || selectedId === null || !Number.isFinite(selectedId)) return;
    const modulesWithSubactions = ['xtam_vms', 'alarmas', 'administracion', 'seguridad'];
    const invalidSubmodule = draft.modules.some(
      (m) =>
        modulesWithSubactions.includes(m.moduleKey) &&
        m.canAccess &&
        !m.actions.some((a) => a.enabled)
    );
    if (invalidSubmodule) {
      showError(
        'Cada módulo con submódulos activo debe tener al menos un submódulo habilitado antes de guardar.'
      );
      return;
    }
    const xtamMod = draft.modules.find((m) => m.moduleKey === 'xtam_vms');
    if (
      xtamMod &&
      xtamMod.canAccess &&
      xtamMod.recordingMaxHoursBack != null &&
      (!Number.isFinite(xtamMod.recordingMaxHoursBack) || xtamMod.recordingMaxHoursBack < 1)
    ) {
      showError(
        'Si la restricción de horas de previsualización está activa, debe indicar un valor de al menos 1 hora.'
      );
      return;
    }
    setSaving(true);
    try {
      const modulesPayload = draft.modules.map((m) => {
        const entry: {
          moduleKey: string;
          canAccess: boolean;
          actions?: Record<string, boolean>;
          recordingMaxHoursBack?: number | null;
        } = { moduleKey: m.moduleKey, canAccess: m.canAccess };
        if (modulesWithSubactions.includes(m.moduleKey) && m.canAccess) {
          const actions: Record<string, boolean> = {};
          m.actions.forEach((a: RbacModuleAction) => {
            actions[a.actionKey] = a.enabled;
          });
          entry.actions = actions;
        }
        if (m.moduleKey === 'xtam_vms') {
          entry.recordingMaxHoursBack = m.recordingMaxHoursBack;
        }
        return entry;
      });
      const updated = await rbacApi.updateRolePermissions(selectedId, modulesPayload);
      setDraft({
        ...updated,
        modules: normalizeModules(updated.modules)
      });
      showSuccess('Permisos guardados');
    } catch (e: any) {
      showError(e.response?.data?.error?.message || e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (user && !canAccessSecurityAdmin(user)) {
    return null;
  }

  return (
    <div className="roles-page">
      <div className="roles-header">
        <div className="roles-header-text">
          <h1>
            <Shield size={28} className="roles-title-icon" aria-hidden />
            Gestión de Roles
          </h1>
          <p className="roles-subtitle">Configura los permisos de acceso por rol</p>
        </div>
        <button
          type="button"
          className="roles-save-btn"
          onClick={handleSave}
          disabled={saving || !draft}
        >
          <Save size={18} />
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>

      <div className="roles-layout">
        <aside className="roles-sidebar">
          <h2 className="roles-sidebar-title">Roles del Sistema</h2>
          {loading ? (
            <p className="roles-muted">Cargando…</p>
          ) : (
            <ul className="roles-list">
              {roles.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className={`roles-card ${selectedId === r.id ? 'selected' : ''}`}
                    onClick={() => setSelectedId(r.id)}
                  >
                    <span className="roles-card-name">{r.name}</span>
                    <span className="roles-card-desc">{r.description || '—'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <main className="roles-detail">
          {!draft ? (
            <p className="roles-muted">Selecciona un rol</p>
          ) : (
            <>
              <div className="roles-detail-head">
                <div className="roles-detail-role-name">{draft.role.name}</div>
                <p className="roles-detail-summary">{summary}</p>
              </div>

              <div className="roles-accordion">
                {draft.modules.map((m: RbacModulePermission) => {
                  const isOpen = expanded.has(m.moduleKey);
                  return (
                    <div key={m.moduleKey} className="roles-acc-item">
                      <button
                        type="button"
                        className="roles-acc-header"
                        onClick={() => toggleExpand(m.moduleKey)}
                      >
                        <span className="roles-acc-icon">{moduleIcon(m.moduleKey)}</span>
                        <span className="roles-acc-label">{m.label}</span>
                        <span className={`roles-acc-chevron ${isOpen ? 'open' : ''}`}>▾</span>
                      </button>
                      {isOpen && (
                        <div className="roles-acc-body">
                          <label className="roles-toggle-row">
                            <span>Acceso al módulo</span>
                            <RolesSwitch
                              checked={m.canAccess}
                              onChange={(next) => setModuleAccess(m.moduleKey, next)}
                              ariaLabel={`Acceso al módulo ${m.label}`}
                            />
                          </label>
                          {m.canAccess && m.actions.length > 0 && (
                            <div className="roles-subactions">
                              {m.actions.map((a: RbacModuleAction) => (
                                <label key={a.actionKey} className="roles-toggle-row sub">
                                  <span>{a.label}</span>
                                  <RolesSwitch
                                    checked={a.enabled}
                                    onChange={(next) =>
                                      setActionEnabled(m.moduleKey, a.actionKey, next)
                                    }
                                    ariaLabel={a.label}
                                  />
                                </label>
                              ))}
                            </div>
                          )}
                          {m.canAccess && m.moduleKey === 'xtam_vms' && (
                            <div className="roles-max-hours-section">
                              <label className="roles-toggle-row sub">
                                <span>Restringir horas de previsualización</span>
                                <RolesSwitch
                                  checked={m.recordingMaxHoursBack != null}
                                  onChange={(next) =>
                                    setRecordingMaxHoursBack(
                                      m.moduleKey,
                                      next ? 2 : null
                                    )
                                  }
                                  ariaLabel="Restringir horas de previsualización"
                                />
                              </label>
                              {m.recordingMaxHoursBack != null && (
                                <div className="roles-max-hours-input-row">
                                  <label className="roles-max-hours-label">
                                    Máximo de horas hacia atrás
                                  </label>
                                  <input
                                    type="number"
                                    min={1}
                                    max={720}
                                    className="roles-max-hours-input"
                                    value={m.recordingMaxHoursBack}
                                    onChange={(e) => {
                                      const v = parseInt(e.target.value, 10);
                                      setRecordingMaxHoursBack(
                                        m.moduleKey,
                                        Number.isFinite(v) && v >= 1 ? v : 1
                                      );
                                    }}
                                  />
                                  <span className="roles-max-hours-hint">
                                    El usuario solo podrá previsualizar y descargar grabaciones de las últimas{' '}
                                    {m.recordingMaxHoursBack} hora{m.recordingMaxHoursBack !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default RolesPage;
